import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface User {
  id: string;
  full_name: string;
  email?: string;
  phone?: string | null;
  roles?: string[];
  avatar?: string | null;
  membership_status?: string | null;
  last_active_at?: string | null;
  member_since?: string | null;
  platform?: string | null;
  app_version?: string | null;
  created_at: string;
}

type LatestClientSnapshot = {
  user_id?: string | null;
  platform?: string | null;
  app_version?: string | null;
  created_at: string;
};

export type UsersResult = {
  users: User[];
  totalCount: number;
};

/** Window in ms for considering a user "online" (matches UsersPage) */
const ONLINE_WINDOW_MS = 5 * 60 * 1000;

export type UseUsersParams = {
  page: number;
  pageSize: number;
  search?: string;
  roleFilter?: string;
  /** When true, only return users with last_active_at within ONLINE_WINDOW_MS */
  onlineOnly?: boolean;
  /** When true, order by last_active_at desc (latest first) so pagination reflects entire list */
  orderByRecent?: boolean;
};

export const useUsers = ({
  page,
  pageSize,
  search,
  roleFilter,
  onlineOnly = false,
  orderByRecent = false,
}: UseUsersParams) => {
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : 100;
  const from = (safePage - 1) * safePageSize;
  const to = from + safePageSize - 1;
  const normalizedSearch = search?.trim();
  const normalizedRole = roleFilter?.trim() ?? 'all';

  return useQuery<UsersResult>({
    queryKey: [
      'users',
      safePage,
      safePageSize,
      normalizedSearch ?? '',
      normalizedRole,
      onlineOnly,
      orderByRecent,
    ],
    queryFn: async () => {
      const orderByRecentOrOnline = orderByRecent || onlineOnly;
      let query = supabase
        .from('users_directory')
        .select('id, full_name, email, phone, roles, avatar, membership_status, last_active_at, member_since, created_at', {
          count: 'exact',
        });

      if (onlineOnly) {
        const cutoff = new Date(Date.now() - ONLINE_WINDOW_MS).toISOString();
        query = query.gte('last_active_at', cutoff);
      }

      if (normalizedSearch) {
        query = query.or(
          `full_name.ilike.%${normalizedSearch}%,email.ilike.%${normalizedSearch}%,phone.ilike.%${normalizedSearch}%`
        );
      }

      if (normalizedRole && normalizedRole !== 'all') {
        query = query.contains('roles', [normalizedRole]);
      }

      if (orderByRecentOrOnline) {
        query = query.order('last_active_at', { ascending: false, nullsFirst: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      const users = (data as User[]) ?? [];
      const userIds = users.map((user) => user.id).filter(Boolean);

      if (userIds.length > 0) {
        const { data: snapshotRows, error: snapshotError } = await supabase
          .from('app_performance_snapshots')
          .select('user_id, platform, app_version, created_at')
          .in('user_id', userIds)
          .order('created_at', { ascending: false });

        if (!snapshotError && snapshotRows) {
          const latestSnapshotByUser = new Map<string, LatestClientSnapshot>();
          (snapshotRows as LatestClientSnapshot[]).forEach((snapshot) => {
            if (!snapshot.user_id || latestSnapshotByUser.has(snapshot.user_id)) return;
            latestSnapshotByUser.set(snapshot.user_id, snapshot);
          });

          users.forEach((user) => {
            const latestSnapshot = latestSnapshotByUser.get(user.id);
            if (!latestSnapshot) return;
            user.platform = latestSnapshot.platform ?? null;
            user.app_version = latestSnapshot.app_version ?? null;
          });
        }
      }

      return {
        users,
        totalCount: count ?? 0,
      };
    },
    staleTime: 5 * 60_000,
  });
};
